import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';

import { useTheme } from '@/app/ThemeContext';
import { Container } from '@/components/legacy-ui';
import { AllIntegrations, useIntegration } from '@/hooks/useIntegration';
import SetupModal from '@/sections/SetupModal';
import { useGlobalState } from '@/state/global.state';
import { availableNotifications, Integration, IntegrationMeta } from '@/types';

/**
 * Filter type for notifications drawer
 * - 'email': Show only email notification option
 * - 'webhook': Show only outgoing webhook option
 * - 'integration': Show only integrations (Slack, Teams, Jira, etc.)
 * - undefined: Show all notification options
 */
export type NotificationFilterType = 'email' | 'webhook' | 'integration';

/**
 * NotificationsDrawer
 *
 * Drawer component for adding new notification integrations.
 * Supports filtering by type (email, webhook, or integrations).
 *
 * The filterType is controlled by global state to allow parent components
 * to open the drawer with a specific filter pre-applied.
 *
 * Used in:
 * - NotificationSettingsCards (via global state)
 * - Settings page drawers
 */
export function NotificationsDrawer() {
  const { currentTheme } = useTheme();
  const { drawerState } = useGlobalState();
  const filterType = drawerState.notificationsDrawerFilterType;

  const {
    data: { connectedIntegrations },
  } = useIntegration();
  const [selectedNotification, setSelectedNotification] =
    useState<IntegrationMeta>();

  const isIntegrationConnected = (integrationId: string) => {
    return connectedIntegrations.some(conn => conn.member === integrationId);
  };

  // Filter available notifications based on filterType
  const filteredNotifications = useMemo(() => {
    if (!filterType) {
      return availableNotifications;
    }

    return availableNotifications.filter(notification => {
      switch (filterType) {
        case 'email':
          return notification === Integration.email;
        case 'webhook':
          return notification === Integration.webhook;
        case 'integration':
          // Everything except email and webhook
          return (
            notification !== Integration.email &&
            notification !== Integration.webhook
          );
        default:
          return true;
      }
    });
  }, [filterType]);

  // Get drawer title based on filter type
  const getDrawerTitle = () => {
    switch (filterType) {
      case 'email':
        return 'Add Email Notification';
      case 'webhook':
        return 'Add Outgoing Webhook';
      case 'integration':
        return 'Add Integration';
      default:
        return 'Push Notifications';
    }
  };

  // Get section title based on filter type
  const getSectionTitle = () => {
    switch (filterType) {
      case 'email':
        return 'Email Configuration';
      case 'webhook':
        return 'Webhook Configuration';
      case 'integration':
        return 'Available Integrations';
      default:
        return 'Ticketing & Messaging';
    }
  };

  return (
    <div className="flex size-full flex-col gap-6 p-6">
      <h1 className="text-2xl font-bold">{getDrawerTitle()}</h1>
      <Container className="size-full overflow-auto p-9">
        <h2 className="text-lg font-bold">{getSectionTitle()}</h2>
        <div className="mt-3 flex flex-wrap gap-3">
          {filteredNotifications.map(notification => {
            const integrationMeta = AllIntegrations[notification];
            const isConnected = isIntegrationConnected(integrationMeta.id);

            return (
              <div
                className="border-chariot-stroke flex w-[312px] gap-3 rounded-md border-2 px-5 py-3"
                key={notification}
              >
                <img
                  src={`/icons/${currentTheme}/${integrationMeta.logo}`}
                  alt={integrationMeta.name}
                  className="size-5"
                />
                <div className="flex flex-col gap-2">
                  <h1 className="text-sm font-bold">{integrationMeta.name}</h1>
                  <p className="text-chariot-text-secondar flex-1 text-sm">
                    {integrationMeta.description}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-fit rounded-full"
                    onClick={() => {
                      setSelectedNotification(integrationMeta);
                    }}
                  >{isConnected ? 'Add Another Account' : 'Connect'}</Button>
                </div>
              </div>
            );
          })}
        </div>
      </Container>
      {Boolean(selectedNotification) && (
        <SetupModal
          isOpen={Boolean(selectedNotification)}
          onClose={() => setSelectedNotification(undefined)}
          onComplete={() => setSelectedNotification(undefined)}
          selectedIntegration={undefined}
          formData={{}}
          integration={selectedNotification}
          hideAddLater
          parseFormData={data => {
            const value =
              data.username === 'email'
                ? data.email
                : data.username === 'jira'
                  ? data.value
                  : data.webhook;

            return { ...data, value };
          }}
        />
      )}
    </div>
  );
}
